<template>
  <PayOpsModalShell
    :visible="visible"
    :title="`Preview: ${actionLabel}`"
    subtitle="Generate and review the exact changes before execution"
    icon="fa-eye"
    :tone="modalTone"
    size="xl"
    :close-disabled="isExecuting"
    @close="$emit('close')"
  >
    <!-- Reason input (required to load preview, MIN 10) -->
    <div class="mb-3">
      <label class="form-label fw-semibold small">Reason (min 10 characters)</label>
      <textarea
        v-model="reason"
        class="form-control border border-primary"
        rows="2"
        placeholder="Explain why this amendment is needed..."
        :disabled="isExecuting"
      />
      <div v-if="reason && reason.length < 10" class="form-text text-danger small">
        Reason must be at least 10 characters.
      </div>
    </div>

    <div class="d-flex justify-content-end mb-3">
      <button
        class="btn btn-sm btn-outline-primary"
        :disabled="!canPreview || isPreviewing"
        @click="loadPreview"
      >
        <span v-if="isPreviewing">
          <span class="spinner-border spinner-border-sm me-1"></span>Generating preview...
        </span>
        <span v-else
          ><i class="fa fa-refresh me-1"></i
          >{{ preview ? 'Re-generate preview' : 'Generate preview' }}</span
        >
      </button>
    </div>

    <div v-if="previewError" class="alert alert-danger outline">
      <i class="fa fa-exclamation-triangle me-2"></i>{{ previewError }}
    </div>

    <template v-if="preview">
      <!-- Diff -->
      <div class="mb-3">
        <h6 class="text-muted">Changes</h6>
        <p v-if="diffEntries.length === 0" class="text-muted small mb-0">No field changes.</p>
        <table v-else class="table table-sm">
          <thead>
            <tr>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in diffEntries" :key="entry.field">
              <td class="small fw-semibold">{{ entry.field }}</td>
              <td class="small text-danger">{{ stringifyValue(entry.before) }}</td>
              <td class="small text-success">{{ stringifyValue(entry.after) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- State transition -->
      <div class="mb-3">
        <h6 class="text-muted">State Transition</h6>
        <span class="badge bg-secondary">{{ preview.stateTransition?.from }}</span>
        <i class="fa fa-arrow-right mx-2"></i>
        <span class="badge bg-primary">{{ preview.stateTransition?.to }}</span>
      </div>

      <!-- Totals -->
      <div v-if="preview.totals" class="mb-3">
        <h6 class="text-muted">Totals</h6>
        <dl class="row small mb-0">
          <dt class="col-sm-4">Before</dt>
          <dd class="col-sm-8">
            {{ formatCurrency(preview.totals.beforeTotal, preview.totals.currency) }}
          </dd>
          <dt class="col-sm-4">After</dt>
          <dd class="col-sm-8">
            {{ formatCurrency(preview.totals.afterTotal, preview.totals.currency) }}
          </dd>
        </dl>
      </div>

      <!-- Downstream effects -->
      <div class="mb-3">
        <h6 class="text-muted">Downstream Effects</h6>
        <ul class="small mb-0">
          <li>
            Invoice generation:
            {{ triggersInvoice ? 'will be triggered' : 'not triggered' }}
          </li>
          <li>
            Payment capture:
            {{ captureOnAmend ? 'immediate (capture on amend)' : 'deferred' }}
          </li>
        </ul>
      </div>

      <div v-if="preview.warnings?.length" class="mb-3">
        <h6 class="text-warning">Warnings</h6>
        <div
          v-for="(w, idx) in preview.warnings"
          :key="idx"
          class="alert alert-warning outline small py-1 px-2 mb-1"
        >
          <i class="fa fa-exclamation-triangle me-1"></i>{{ w }}
        </div>
      </div>

      <div v-if="preview.blockers?.length" class="mb-3">
        <h6 class="text-danger">Blockers</h6>
        <div
          v-for="(b, idx) in preview.blockers"
          :key="idx"
          class="alert alert-danger outline small py-1 px-2 mb-1"
        >
          <i class="fa fa-ban me-1"></i>{{ b }}
        </div>
      </div>

      <p class="text-muted small mb-0 d-flex flex-wrap align-items-baseline gap-1">
        <span>Preview expires at</span>
        <ZonedDateTime
          :value="preview.expiresAt"
          :zone="userTimezone"
          kind="user"
          layout="inline"
        />.
      </p>
    </template>
    <template #footer>
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="isExecuting"
        @click="$emit('close')"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn"
        :class="executeButtonClass"
        :disabled="!canExecute"
        @click="execute"
      >
        <span v-if="isExecuting">
          <span class="spinner-border spinner-border-sm me-1"></span>Executing...
        </span>
        <span v-else><i class="fa fa-play me-1"></i>Confirm &amp; Execute</span>
      </button>
    </template>

    <div v-if="executionResult" class="mt-3 rounded p-3" :class="resultClass">
      <div v-if="executionResult.success" class="text-success">
        <i class="fa fa-check-circle me-2"></i>Amendment executed successfully.
      </div>
      <div v-else-if="executionResult.partial" class="text-warning">
        <i class="fa fa-exclamation-circle me-2"></i>
        Order amended but invoice generation or Odoo verification failed. The order state has been
        updated.
      </div>
      <div v-else-if="executionResult.stale" class="text-warning">
        <i class="fa fa-clock-o me-2"></i>
        The order changed since this preview was generated. Re-generate the preview and try again.
      </div>
      <div v-else class="text-danger">
        <i class="fa fa-times-circle me-2"></i>{{ executionResult.message || 'Execution failed.' }}
      </div>
    </div>
  </PayOpsModalShell>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import {
  runPreview,
  runExecute,
} from '@/services/ryd-pay-operations/order-amendments/amendment-api';
import { formatCurrency } from '@/utils/display-formatters';
import { buildProductAmendments } from '@/services/ryd-pay-operations/order-amendments/product-amendments';
import PayOpsModalShell from '../../shared/PayOpsModalShell.vue';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';

const props = defineProps({
  visible: { type: Boolean, default: false },
  orderUuid: { type: String, required: true },
  action: { type: String, required: true },
  editedProducts: { type: Array, default: () => [] },
  editedBilling: { type: Object, default: () => null },
  captureOnAmend: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'executed']);
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);

const preview = ref(null);
const previewError = ref('');
const isPreviewing = ref(false);
const isExecuting = ref(false);
const reason = ref('');
const executionResult = ref(null);

const actionLabel = computed(() => {
  switch (props.action) {
    case 'completeManually':
      return 'Complete Manually';
    case 'cancelManually':
      return 'Cancel Manually';
    case 'forceCompleteManually':
      return 'Force Complete';
    case 'refundAndCancelManually':
      return 'Refund & Cancel';
    default:
      return props.action;
  }
});

const modalTone = computed(() =>
  props.action === 'cancelManually' || props.action === 'refundAndCancelManually'
    ? 'danger'
    : 'primary',
);

const executeButtonClass = computed(() =>
  props.action === 'cancelManually' || props.action === 'refundAndCancelManually'
    ? 'btn-danger'
    : 'btn-primary',
);

const canPreview = computed(() => reason.value.length >= 10);

const canExecute = computed(
  () =>
    !!preview.value &&
    !isExecuting.value &&
    (preview.value.blockers?.length ?? 0) === 0 &&
    !executionResult.value?.success,
);

const resultClass = computed(() => {
  if (!executionResult.value) return '';
  if (executionResult.value.success) return 'bg-success bg-opacity-10';
  if (executionResult.value.partial) return 'bg-warning bg-opacity-10';
  return 'bg-danger bg-opacity-10';
});

const diffEntries = computed(() => {
  if (!preview.value?.diff) return [];
  return Object.entries(preview.value.diff).map(([field, value]) => ({
    field,
    before: value?.before ?? null,
    after: value?.after ?? null,
  }));
});

const triggersInvoice = computed(
  () => props.action === 'completeManually' || props.action === 'forceCompleteManually',
);

/** Returns the OrderAmendmentsDto-shaped draft, or null (with `previewError` set) when a product row is incomplete. */
function buildDraft() {
  const { errors, ...products } = buildProductAmendments(props.editedProducts);
  if (errors.length) {
    previewError.value = `Fix the product rows first: ${errors.join('; ')}`;
    return null;
  }
  const draft = { ...products, captureOnAmend: props.captureOnAmend };
  if (props.action === 'refundAndCancelManually') draft.isRefundEligible = true;
  if (props.editedBilling) draft.billingDetails = props.editedBilling;
  return draft;
}

function stringifyValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

async function loadPreview() {
  if (!canPreview.value) return;
  isPreviewing.value = true;
  previewError.value = '';
  preview.value = null;
  executionResult.value = null;
  const draft = buildDraft();
  if (!draft) {
    isPreviewing.value = false;
    return;
  }
  try {
    preview.value = await runPreview(props.orderUuid, {
      action: props.action,
      draft,
      reason: reason.value,
    });
  } catch (err) {
    previewError.value =
      err?.response?.data?.message ?? err?.message ?? 'Failed to generate preview.';
  } finally {
    isPreviewing.value = false;
  }
}

async function execute() {
  if (!canExecute.value || !preview.value) return;
  isExecuting.value = true;
  executionResult.value = null;
  try {
    const result = await runExecute(props.orderUuid, {
      action: props.action,
      previewToken: preview.value.previewToken,
    });
    executionResult.value = result;
    emit('executed', result);
  } catch (err) {
    const status = err?.response?.status;
    executionResult.value = {
      success: false,
      partial: false,
      stale: status === 409,
      message: err?.response?.data?.message ?? err?.message ?? 'Execution failed.',
    };
  } finally {
    isExecuting.value = false;
  }
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      reason.value = '';
      preview.value = null;
      previewError.value = '';
      executionResult.value = null;
    }
  },
);
</script>
